import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import { loadManifest, saveManifest } from "../utils/idb";

export type AssetKind = "mesh" | "texture";
export type AssetSource = "url" | "blob" | "arraybuffer" | "idb";
export type AssetStatus = "idle" | "loading" | "ready" | "error";

interface AssetSources {
  url?: string;
  idbKey?: string;
  blobUrl?: string;
  /**
   * When a mesh was registered from catalog XML (library / recipe), the `/folder/id/`
   * path prefix used to fetch that asset. Not a mesh geometry URL; excluded from loaders.
   */
  libraryPathPrefix?: string;
}

type AssetSourcesInput = {
  url?: string | null;
  idbKey?: string | null;
  blobUrl?: string | null;
  libraryPathPrefix?: string | null;
};

interface AssetEntryBase<K extends AssetKind> {
  id: string;
  kind: K;
  name: string;
  status: AssetStatus;
  bytes?: number;
  hash?: string;
  error?: string;
  thumbnailDataUrl?: string;
  sources: AssetSources;
  createdAt: number;
  updatedAt: number;
}

export interface MeshAssetEntry extends AssetEntryBase<"mesh"> {
  vertexCount?: number;
}

export interface TextureAssetEntry extends AssetEntryBase<"texture"> {
  width?: number;
  height?: number;
}

export type AssetEntry = MeshAssetEntry | TextureAssetEntry;

type AssetRegistry = Record<string, AssetEntry>;
type PersistedAssetRecord = Record<string, unknown>;

const isBlobObjectUrl = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith("blob:");

const collectBlobObjectUrls = (entry?: AssetEntry): Set<string> => {
  const urls = new Set<string>();
  if (!entry?.sources) return urls;
  if (isBlobObjectUrl(entry.sources.url)) urls.add(entry.sources.url);
  if (isBlobObjectUrl(entry.sources.blobUrl)) urls.add(entry.sources.blobUrl);
  return urls;
};

const revokeBlobObjectUrl = (url: string) => {
  if (typeof URL === "undefined" || typeof URL.revokeObjectURL !== "function") return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    // Revocation is best-effort cleanup.
  }
};

const revokeBlobUrlsNoLongerReferenced = (previous?: AssetEntry, next?: AssetEntry) => {
  const nextUrls = collectBlobObjectUrls(next);
  collectBlobObjectUrls(previous).forEach((url) => {
    if (!nextUrls.has(url)) revokeBlobObjectUrl(url);
  });
};

const revokeAllBlobObjectUrls = (entries: AssetRegistry) => {
  const urls = new Set<string>();
  Object.values(entries).forEach((entry) => {
    collectBlobObjectUrls(entry).forEach((url) => urls.add(url));
  });
  urls.forEach(revokeBlobObjectUrl);
};

interface AssetRegistrationBase<K extends AssetKind> {
  id: string;
  kind: K;
  name?: string;
  status?: AssetStatus;
  bytes?: number;
  hash?: string;
  error?: string | null;
  thumbnailDataUrl?: string | null;
  source?: AssetSource | null;
  ref?: string | null;
  sources?: AssetSourcesInput;
}

export interface MeshAssetRegistration extends AssetRegistrationBase<"mesh"> {
  vertexCount?: number | null;
}

export interface TextureAssetRegistration extends AssetRegistrationBase<"texture"> {
  width?: number | null;
  height?: number | null;
}

export type AssetRegistration = MeshAssetRegistration | TextureAssetRegistration;

interface AssetStoreState {
  entries: AssetRegistry;
  actions: AssetActions;
}

interface AssetActions {
  registerAssets: (entries: AssetRegistration[]) => void;
  registerMeshes: (entries: MeshAssetRegistration[]) => void;
  registerTextures: (entries: TextureAssetRegistration[]) => void;
  setAssetStatus: (id: string, status: AssetStatus, options?: { error?: string | null }) => void;
  setAssetThumbnail: (id: string, dataUrl: string) => void;
  setMeshVertexCount: (id: string, vertexCount: number | null | undefined) => void;
  setTextureDimensions: (
    id: string,
    width: number | null | undefined,
    height: number | null | undefined
  ) => void;
  rehydrateFromPersisted: () => Promise<void>;
  getAssetById: (id: string) => AssetEntry | undefined;
  clearAll: () => void;
}

const useAssetStoreInternal = create<AssetStoreState>()(
  devtools(
    (set, get) => {
      const upsertEntries = (registrations: AssetRegistration[], actionName: string) => {
        if (!Array.isArray(registrations) || registrations.length === 0) return;
        const now = Date.now();
        set(
          (state) => {
            let changed = false;
            const next: AssetRegistry = { ...state.entries };

            registrations.forEach((registration) => {
              if (!registration || typeof registration !== "object") return;
              const existing = next[registration.id];
              const updated = applyRegistration(existing, registration, now);
              if (updated && updated !== existing) {
                next[updated.id] = updated;
                revokeBlobUrlsNoLongerReferenced(existing, updated);
                changed = true;
              }
            });

            return changed ? { entries: next } : state;
          },
          false,
          actionName
        );
      };

      const persistManifest = (entries: AssetRegistry) => {
        // Exclude entries whose only source is a blob: URL — those are session-scoped
        // and become dead links after a page reload.
        const persistable = Object.values(entries).filter((entry) => {
          const s = entry?.sources;
          if (!s) return true;
          return s.idbKey || s.blobUrl || !s.url?.startsWith("blob:");
        });
        Promise.resolve(saveManifest(persistable)).catch(() => {});
      };

      const mutateEntry = (
        id: string,
        mutator: (entry: AssetEntry, now: number) => AssetEntry,
        actionName: string,
        { persist }: { persist?: boolean } = {}
      ) => {
        const now = Date.now();
        set(
          (state) => {
            const prev = state.entries[id];
            if (!prev) return state;
            const nextEntry = mutator(prev, now);
            if (nextEntry === prev) return state;
            const entries = { ...state.entries, [id]: nextEntry };
            if (persist) persistManifest(entries);
            return { entries };
          },
          false,
          actionName
        );
      };

      const actions: AssetActions = {
        registerAssets: (entries) => upsertEntries(entries, "registerAssets"),

        registerMeshes: (entries) => upsertEntries(entries, "registerMeshes"),

        registerTextures: (entries) => upsertEntries(entries, "registerTextures"),

        setAssetStatus: (id, status, options) => {
          mutateEntry(
            id,
            (entry, now) => {
              let changed = false;
              let nextError = entry.error;
              if (entry.status !== status) {
                changed = true;
              }
              if (options && Object.prototype.hasOwnProperty.call(options, "error")) {
                const desired = options.error ?? undefined;
                if (desired !== entry.error) {
                  nextError = desired;
                  changed = true;
                }
              }
              if (!changed) return entry;
              return {
                ...entry,
                status,
                error: nextError,
                updatedAt: now,
              };
            },
            "setAssetStatus"
          );
        },

        setAssetThumbnail: (id, dataUrl) => {
          mutateEntry(
            id,
            (entry, now) => {
              if (entry.thumbnailDataUrl === dataUrl) return entry;
              return {
                ...entry,
                thumbnailDataUrl: dataUrl,
                updatedAt: now,
              };
            },
            "setAssetThumbnail",
            { persist: true }
          );
        },

        setMeshVertexCount: (id, vertexCount) => {
          mutateEntry(
            id,
            (entry, now) => {
              if (entry.kind !== "mesh") return entry;
              if (
                entry.vertexCount === vertexCount ||
                (vertexCount == null && entry.vertexCount == null)
              ) {
                return entry;
              }
              const next: MeshAssetEntry = {
                ...entry,
                vertexCount: vertexCount ?? undefined,
                updatedAt: now,
              };
              return next;
            },
            "setMeshVertexCount"
          );
        },

        setTextureDimensions: (id, width, height) => {
          mutateEntry(
            id,
            (entry, now) => {
              if (entry.kind !== "texture") return entry;
              const nextWidth = width ?? undefined;
              const nextHeight = height ?? undefined;
              if (entry.width === nextWidth && entry.height === nextHeight) return entry;
              return {
                ...entry,
                width: nextWidth,
                height: nextHeight,
                updatedAt: now,
              };
            },
            "setTextureDimensions"
          );
        },

        rehydrateFromPersisted: async () => {
          try {
            const raw = (await loadManifest()) || [];
            if (!Array.isArray(raw) || raw.length === 0) return;
            const registrations: AssetRegistration[] = [];
            raw.forEach((entry) => {
              const reg = convertPersistedEntry(entry);
              if (reg) registrations.push(reg);
            });
            // Discard entries whose only source is a blob: URL — those are session-scoped
            // and become dead links after a page reload.
            const liveRegistrations = registrations.filter((reg) => {
              const s = reg.sources;
              if (!s) return true;
              return s.idbKey || s.blobUrl || !s.url?.startsWith("blob:");
            });
            if (liveRegistrations.length) {
              upsertEntries(liveRegistrations, "rehydrateFromPersisted");
            }
          } catch {
            // non-fatal
          }
        },

        getAssetById: (id) => get().entries[id],

        clearAll: () => {
          set(
            (state) => {
              revokeAllBlobObjectUrls(state.entries);
              return { entries: {} };
            },
            false,
            "clearAll"
          );
        },
      };

      return {
        entries: {},
        actions,
      };
    },
    { name: "AssetStore" }
  )
);

const applyRegistration = (
  existing: AssetEntry | undefined,
  registration: AssetRegistration,
  now: number
): AssetEntry | undefined => {
  if (!registration.id) return existing;

  if (!existing) {
    return createEntryFromRegistration(registration, now);
  }

  if (existing.kind !== registration.kind) {
    return createEntryFromRegistration(registration, now);
  }

  let changed = false;
  const next: AssetEntry = { ...existing };

  if (registration.name && registration.name !== existing.name) {
    next.name = registration.name;
    changed = true;
  }

  if (typeof registration.status === "string" && registration.status !== existing.status) {
    next.status = registration.status;
    changed = true;
  }

  if (registration.bytes !== undefined && registration.bytes !== existing.bytes) {
    next.bytes = registration.bytes;
    changed = true;
  }

  if (registration.hash !== undefined && registration.hash !== existing.hash) {
    next.hash = registration.hash ?? undefined;
    changed = true;
  }

  if (Object.prototype.hasOwnProperty.call(registration, "thumbnailDataUrl")) {
    const desired = registration.thumbnailDataUrl ?? undefined;
    if (next.thumbnailDataUrl !== desired) {
      next.thumbnailDataUrl = desired;
      changed = true;
    }
  }

  if (Object.prototype.hasOwnProperty.call(registration, "error")) {
    const desired = registration.error ?? undefined;
    if (desired !== existing.error) {
      next.error = desired;
      changed = true;
    }
  }

  const sourcesInput = toSourcesInput(registration);
  const { merged: mergedSources, changed: sourcesChanged } = mergeSources(
    existing.sources,
    sourcesInput
  );
  if (sourcesChanged) {
    next.sources = mergedSources;
    changed = true;
  }

  if (existing.kind === "mesh") {
    const meshReg = registration as MeshAssetRegistration;
    const meshNext = next as MeshAssetEntry;
    if (Object.prototype.hasOwnProperty.call(meshReg, "vertexCount")) {
      const desired = meshReg.vertexCount ?? undefined;
      if (meshNext.vertexCount !== desired) {
        meshNext.vertexCount = desired;
        changed = true;
      }
    }
  }

  if (existing.kind === "texture") {
    const texReg = registration as TextureAssetRegistration;
    const texNext = next as TextureAssetEntry;
    if (Object.prototype.hasOwnProperty.call(texReg, "width")) {
      const desired = texReg.width ?? undefined;
      if (texNext.width !== desired) {
        texNext.width = desired;
        changed = true;
      }
    }
    if (Object.prototype.hasOwnProperty.call(texReg, "height")) {
      const desired = texReg.height ?? undefined;
      if (texNext.height !== desired) {
        texNext.height = desired;
        changed = true;
      }
    }
  }

  if (!changed) return existing;

  next.updatedAt = now;
  return next;
};

const createEntryFromRegistration = (
  registration: AssetRegistration,
  timestamp: number
): AssetEntry => {
  const sources = normalizeSourcesInput(toSourcesInput(registration));
  const base: AssetEntryBase<AssetKind> = {
    id: registration.id,
    kind: registration.kind,
    name: registration.name || registration.id,
    status: registration.status ?? "idle",
    bytes: registration.bytes,
    hash: registration.hash ?? undefined,
    error: registration.error ?? undefined,
    thumbnailDataUrl: registration.thumbnailDataUrl ?? undefined,
    sources,
    createdAt: timestamp,
    updatedAt: timestamp,
  };

  if (registration.kind === "mesh") {
    const meshReg = registration as MeshAssetRegistration;
    const meshEntry: MeshAssetEntry = {
      ...base,
      kind: "mesh",
      vertexCount: meshReg.vertexCount ?? undefined,
    };
    return meshEntry;
  }

  const texReg = registration as TextureAssetRegistration;
  const texEntry: TextureAssetEntry = {
    ...base,
    kind: "texture",
    width: texReg.width ?? undefined,
    height: texReg.height ?? undefined,
  };
  return texEntry;
};

const isRecord = (value: unknown): value is PersistedAssetRecord =>
  !!value && typeof value === "object";

const getRecordProp = (raw: PersistedAssetRecord, key: string): unknown => raw[key];

const getNestedRecordProp = (
  raw: PersistedAssetRecord,
  parentKey: string,
  childKey: string
): unknown => {
  const parent = getRecordProp(raw, parentKey);
  return isRecord(parent) ? parent[childKey] : undefined;
};

const convertPersistedEntry = (raw: unknown): AssetRegistration | null => {
  if (!isRecord(raw)) return null;
  const { id, kind } = raw;
  if (typeof id !== "string") return null;
  if (kind !== "mesh" && kind !== "texture") return null;
  const sources = extractSources(raw);
  const base: AssetRegistrationBase<AssetKind> = {
    id,
    kind,
    name: typeof raw.name === "string" && raw.name.length > 0 ? raw.name : undefined,
    status: "idle" as AssetStatus,
    bytes: typeof raw.bytes === "number" ? raw.bytes : undefined,
    hash: typeof raw.hash === "string" ? raw.hash : undefined,
    thumbnailDataUrl: resolveThumbnail(raw) ?? undefined,
  };

  if (hasSourcesInput(sources)) {
    base.sources = sources;
  }

  if (kind === "mesh") {
    const meshReg: MeshAssetRegistration = {
      ...base,
      kind: "mesh",
      vertexCount: resolveNumber(
        raw.vertexCount ?? getNestedRecordProp(raw, "meta", "vertexCount")
      ),
    };
    return meshReg;
  }

  const textureReg: TextureAssetRegistration = {
    ...base,
    kind: "texture",
    width: resolveNumber(raw.width),
    height: resolveNumber(raw.height),
  };
  return textureReg;
};

const resolveThumbnail = (raw: PersistedAssetRecord): string | undefined => {
  const direct = typeof raw.thumbnailDataUrl === "string" ? raw.thumbnailDataUrl : undefined;
  if (direct && direct.startsWith("data:")) return direct;
  const legacyThumbnail = getNestedRecordProp(raw, "meta", "thumbnail");
  const legacy = typeof legacyThumbnail === "string" ? legacyThumbnail : undefined;
  if (legacy && legacy.startsWith("data:")) return legacy;
  return undefined;
};

const resolveNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return undefined;
};

const toSourcesInput = (registration: AssetRegistration): AssetSourcesInput | undefined => {
  let result: AssetSourcesInput | undefined;

  if (registration.sources) {
    result = { ...registration.sources };
  }

  if (registration.source && registration.ref) {
    const ensure = (): AssetSourcesInput => {
      if (!result) result = {};
      return result;
    };
    switch (registration.source) {
      case "idb":
        {
          const target = ensure();
          if (target.idbKey === undefined) target.idbKey = registration.ref;
        }
        break;
      case "blob":
      case "arraybuffer":
        {
          const target = ensure();
          if (target.blobUrl === undefined) target.blobUrl = registration.ref;
        }
        break;
      case "url":
        {
          const target = ensure();
          if (target.url === undefined) target.url = registration.ref;
        }
        break;
      default:
        break;
    }
  }

  return result;
};

const normalizeSourcesInput = (input?: AssetSourcesInput): AssetSources => {
  const sources: AssetSources = {};
  if (!input) return sources;
  if (typeof input.url === "string" && input.url.length > 0) sources.url = input.url;
  if (typeof input.idbKey === "string" && input.idbKey.length > 0) sources.idbKey = input.idbKey;
  if (typeof input.blobUrl === "string" && input.blobUrl.length > 0)
    sources.blobUrl = input.blobUrl;
  if (typeof input.libraryPathPrefix === "string" && input.libraryPathPrefix.length > 0) {
    sources.libraryPathPrefix = input.libraryPathPrefix;
  }
  return sources;
};

const hasSourcesInput = (input?: AssetSourcesInput): boolean => {
  if (!input) return false;
  return Boolean(
    (typeof input.url === "string" && input.url.length > 0) ||
    (typeof input.idbKey === "string" && input.idbKey.length > 0) ||
    (typeof input.blobUrl === "string" && input.blobUrl.length > 0)
  );
};

const mergeSources = (
  existing: AssetSources,
  input?: AssetSourcesInput
): { merged: AssetSources; changed: boolean } => {
  if (!input) return { merged: existing, changed: false };

  const merged: AssetSources = { ...existing };
  let changed = false;

  const update = (key: keyof AssetSources, hasProp: boolean, value?: string | null) => {
    if (!hasProp) return;
    const nextValue = value ?? undefined;
    if (merged[key] !== nextValue) {
      if (nextValue === undefined) {
        delete merged[key];
      } else {
        merged[key] = nextValue;
      }
      changed = true;
    }
  };

  update("url", Object.prototype.hasOwnProperty.call(input, "url"), input.url ?? undefined);
  update(
    "idbKey",
    Object.prototype.hasOwnProperty.call(input, "idbKey"),
    input.idbKey ?? undefined
  );
  update(
    "blobUrl",
    Object.prototype.hasOwnProperty.call(input, "blobUrl"),
    input.blobUrl ?? undefined
  );
  update(
    "libraryPathPrefix",
    Object.prototype.hasOwnProperty.call(input, "libraryPathPrefix"),
    input.libraryPathPrefix ?? undefined
  );

  if (!changed) {
    return { merged: existing, changed: false };
  }

  return { merged, changed: true };
};

const extractSources = (raw: unknown): AssetSourcesInput | undefined => {
  if (!isRecord(raw)) return undefined;
  const result: AssetSourcesInput = {};
  let found = false;

  const rawSources = isRecord(raw.sources) ? raw.sources : undefined;
  if (rawSources) {
    if (typeof rawSources.url === "string" && rawSources.url.length > 0) {
      result.url = rawSources.url;
      found = true;
    }
    if (typeof rawSources.idbKey === "string" && rawSources.idbKey.length > 0) {
      result.idbKey = rawSources.idbKey;
      found = true;
    }
    if (typeof rawSources.blobUrl === "string" && rawSources.blobUrl.length > 0) {
      result.blobUrl = rawSources.blobUrl;
      found = true;
    }
    if (
      typeof rawSources.libraryPathPrefix === "string" &&
      rawSources.libraryPathPrefix.length > 0
    ) {
      result.libraryPathPrefix = rawSources.libraryPathPrefix;
      found = true;
    }
  }

  const legacySource = typeof raw.source === "string" ? (raw.source as AssetSource) : undefined;
  const legacyRef = typeof raw.ref === "string" ? (raw.ref as string) : undefined;
  if (legacySource && legacyRef) {
    switch (legacySource) {
      case "idb":
        if (!result.idbKey) result.idbKey = legacyRef;
        found = true;
        break;
      case "blob":
      case "arraybuffer":
        if (!result.blobUrl) result.blobUrl = legacyRef;
        found = true;
        break;
      case "url":
        if (!result.url) result.url = legacyRef;
        found = true;
        break;
      default:
        break;
    }
  }

  return found ? result : undefined;
};

// Selector hooks
export const useAssetEntries = () => useAssetStoreInternal((state) => state.entries);
export const useAssetEntry = (id: string) => useAssetStoreInternal((state) => state.entries[id]);
export const useAssetActions = () => useAssetStoreInternal((state) => state.actions);

type MeshLibraryTileData = {
  id: string;
  name: string;
  thumbnailDataUrl: string | null;
  sourceUrl: string;
  sourceIdbKey: string;
  sourceBlobUrl: string;
  sourceLibraryPathPrefix: string;
};

export const useMeshAssetSearchIndex = () =>
  useAssetStoreInternal(
    useShallow((state) => {
      const rows: string[] = [];
      Object.values(state.entries).forEach((entry) => {
        if (entry.kind !== "mesh") return;
        const name = entry.name || entry.id;
        const row = [
          name.toLowerCase(),
          entry.id,
          String(entry.sources?.url || "").toLowerCase(),
          String(entry.sources?.idbKey || "").toLowerCase(),
          String(entry.sources?.blobUrl || "").toLowerCase(),
          String(entry.sources?.libraryPathPrefix || "").toLowerCase(),
        ].join("\u0001");
        rows.push(row);
      });
      rows.sort((a, b) => a.localeCompare(b));
      return rows;
    })
  );

export const useMeshAssetLibraryTile = (id: string): MeshLibraryTileData | null =>
  useAssetStoreInternal(
    useShallow((state) => {
      const entry = state.entries[id];
      if (!entry || entry.kind !== "mesh") return null;
      return {
        id: entry.id,
        name: entry.name || entry.id,
        thumbnailDataUrl:
          typeof entry.thumbnailDataUrl === "string" ? entry.thumbnailDataUrl : null,
        sourceUrl: entry.sources?.url || "",
        sourceIdbKey: entry.sources?.idbKey || "",
        sourceBlobUrl: entry.sources?.blobUrl || "",
        sourceLibraryPathPrefix: entry.sources?.libraryPathPrefix || "",
      };
    })
  );

export const useMeshAssets = () =>
  useAssetStoreInternal(
    useShallow((state) => filterEntries<MeshAssetEntry>(state.entries, "mesh"))
  );

export const useTextureAssets = () =>
  useAssetStoreInternal(
    useShallow((state) => filterEntries<TextureAssetEntry>(state.entries, "texture"))
  );

const filterEntries = <T extends AssetEntry>(
  entries: AssetRegistry,
  kind: AssetKind
): Record<string, T> => {
  const filtered: Record<string, T> = {};
  Object.entries(entries).forEach(([id, entry]) => {
    if (entry.kind === kind) {
      filtered[id] = entry as T;
    }
  });
  return filtered;
};

// Accessors for environments where hooks are unavailable
export const getAssetActions = () => useAssetStoreInternal.getState().actions;
export const getAssetById = (id: string) =>
  useAssetStoreInternal.getState().actions.getAssetById(id);
export const getAssetEntries = () => useAssetStoreInternal.getState().entries;
